import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { getSupabase } from "../utils";
import type { EntityAutomationConfig, AutomationKind } from "./entity-automation-types";
import { AUTOMATION_KINDS } from "./entity-automation-types";

export function useListEntityAutomationConfigs(
  entityType?: string,
  options?: { automationKind?: AutomationKind | "all" }
) {
  const automationKind = options?.automationKind ?? AUTOMATION_KINDS.followUpTask;
  return useQuery({
    queryKey: ["entity-automation-configs", entityType ?? "all", automationKind],
    queryFn: async () => {
      const supabase = getSupabase();
      let query = supabase
        .from("entity_automation_config")
        .select("*")
        .order("created_at", { ascending: false });

      if (entityType) {
        query = query.eq("entity_type", entityType);
      }
      if (automationKind !== "all") {
        query = query.eq("automation_kind", automationKind);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as EntityAutomationConfig[];
    },
  });
}

export function useEntityAutomationConfig(
  entityType: string,
  entityId: string,
  options?: { automationKind?: AutomationKind | "all" }
) {
  const automationKind = options?.automationKind ?? AUTOMATION_KINDS.followUpTask;
  return useQuery({
    queryKey: ["entity-automation-config", entityType, entityId, automationKind],
    queryFn: async () => {
      const supabase = getSupabase();
      let query = supabase
        .from("entity_automation_config")
        .select("*")
        .eq("entity_type", entityType)
        .eq("entity_id", entityId);
      if (automationKind !== "all") {
        query = query.eq("automation_kind", automationKind);
      }
      const { data, error } = await query.single();

      if (error && error.code !== "PGRST116") {
        // PGRST116 = no rows returned
        throw error;
      }

      return data as EntityAutomationConfig | null;
    },
  });
}

export function useCreateOrUpdateEntityAutomationConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      config: Omit<EntityAutomationConfig, "id" | "created_at" | "updated_at" | "automation_kind"> & {
        automation_kind?: AutomationKind;
      }
    ) => {
      const supabase = getSupabase();

      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();
      if (authError || !user?.id) {
        throw new Error("User not authenticated");
      }
      const automationKind = config.automation_kind ?? AUTOMATION_KINDS.followUpTask;

      const { data: existing } = await supabase
        .from("entity_automation_config")
        .select("id, created_by")
        .eq("entity_type", config.entity_type)
        .eq("entity_id", config.entity_id)
        .eq("automation_kind", automationKind)
        .single();

      const { created_by: _ignoredCreatedBy, ...configWithoutCreatedBy } = config;

      if (existing) {
        // Preserve original owner; never replace from client payload
        const ownerId = existing.created_by ?? user.id;
        const { data, error } = await supabase
          .from("entity_automation_config")
          .update({
            ...configWithoutCreatedBy,
            automation_kind: automationKind,
            created_by: ownerId,
          })
          .eq("id", existing.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Create new — current user owns this automation
        const { data, error } = await supabase
          .from("entity_automation_config")
          .insert({
            ...configWithoutCreatedBy,
            automation_kind: automationKind,
            created_by: user.id,
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ["entity-automation-config", data.entity_type, data.entity_id],
      });
      void queryClient.invalidateQueries({
        queryKey: ["entity-automation-configs"],
      });
      toast.success("Automation configuration saved");
    },
    onError: (error) => {
      logger.error("Failed to save automation config", error);
      toast.error("Failed to save automation configuration");
    },
  });
}

export function useDeleteEntityAutomationConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      entityType,
      entityId,
      automationKind,
    }: {
      id?: string;
      entityType?: string;
      entityId?: string;
      automationKind?: AutomationKind;
    }) => {
      const supabase = getSupabase();
      let deleteQuery = supabase.from("entity_automation_config").delete();

      if (id) {
        deleteQuery = deleteQuery.eq("id", id);
      } else if (entityType && entityId) {
        deleteQuery = deleteQuery
          .eq("entity_type", entityType)
          .eq("entity_id", entityId)
          .eq("automation_kind", automationKind ?? AUTOMATION_KINDS.followUpTask);
      } else {
        throw new Error("Either id or (entityType and entityId) is required to delete config");
      }

      const { error } = await deleteQuery;

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      if (variables.entityType && variables.entityId) {
        void queryClient.invalidateQueries({
          queryKey: ["entity-automation-config", variables.entityType, variables.entityId],
        });
      }
      void queryClient.invalidateQueries({
        queryKey: ["entity-automation-configs"],
      });
      toast.success("Automation configuration deleted");
    },
    onError: (error) => {
      logger.error("Failed to delete automation config", error);
      toast.error("Failed to delete automation configuration");
    },
  });
}
